/**
 * MonthlyRoundup Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * An advanced auto-scrolling monthly updates display for Council Corner.
 * Features include:
 * - Quantum Auto-Scroll: Only scrolls when component is visible and tab is active
 * - Topic-based content organization with dynamic grouping
 * - Intelligent HTML parsing and content cleaning
 * - IFIA brand color-coded categorization
 * - Advanced visibility detection using Intersection Observer
 * - Page focus detection using Page Visibility API
 * - Pause-on-hover functionality for better user experience
 * - Responsive design with smooth animations
 * - Header extraction and formatting for clean display
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of monthly update objects with ranking, category, colour, and update content
 * @returns {JSX.Element} Monthly roundup auto-scrolling component
 */
import React, { useRef, useEffect, useState } from 'react';
import Parse from 'html-react-parser';
import './MonthlyRoundup.css';

/**
 * MonthlyRoundup Component
 * 
 * Displays monthly council updates in an auto-scrolling format with topic-based organization.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Monthly update data array
 * @returns {JSX.Element} Monthly roundup component
 */
const MonthlyRoundup = ({ data }) => {
    const roundupRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);
    const viewMode = 'topic'; // Fixed to topic view
    const [isAutoScrolling, setIsAutoScrolling] = useState(true);
    const [isVisible, setIsVisible] = useState(false); // Component visibility
    const [isTabActive, setIsTabActive] = useState(true); // Tab focus state

    // Sort data by ranking
    const sortedData = [...data].sort((a, b) => {
        const rankA = a.ranking ? parseInt(a.ranking, 10) : Number.MAX_SAFE_INTEGER;
        const rankB = b.ranking ? parseInt(b.ranking, 10) : Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
    });

    const colorMap = {
        'ORANGE': 'var(--ifia-orange, #eb7b15)',
        'BLUE': 'var(--ifia-blue, #006d92)',
        'PURPLE': 'var(--ifia-purple, #843390)',
        'GREEN': 'var(--ifia-green, #56b32f)',
        'DARK GREEN': 'var(--ifia-dark-green, #385724)',
        'YELLOW': 'var(--ifia-yellow, #e6f563)',
        'RED': 'var(--ifia-red, #bd2130)',
        'LIGHT BLUE': 'var(--ifia-lightblue, #00b4d8)',
        'GREY': 'var(--ifia-grey, #776f6f)',
        'BLACK': 'var(--ifia-black, #000000)',
        'DARK BLUE': 'var(--ifia-dark-blue, #1614fd)',
        'SNOW': 'var(--ifia-snow, #dddddd)'
    };

    /**
     * Maps color names to IFIA brand color values
     * 
     * @param {string} colorName - Color name from data
     * @returns {string} CSS color value
     */
    const getColor = (colorName) => {
        return colorMap[colorName?.toUpperCase()] || '#gray';
    };

    /**
     * Parses HTML content to extract individual update items
     * 
     * Attempts to find list items first, then falls back to styled divs.
     * Cleans up Wingdings symbols and formatting.
     * 
     * @param {string} htmlContent - Raw HTML content to parse
     * @returns {Array} Array of parsed update items with id and content
     */
    const parseUpdates = (htmlContent) => {
        try {
            const container = document.createElement('div');
            container.innerHTML = htmlContent;
            
            // First try to find list items
            let items = container.querySelectorAll('li');
            
            // If no list items found, look for div elements with specific styling
            if (items.length === 0) {
                items = container.querySelectorAll('div[style*="margin-left:.19in"]');
            }
            
            const parsedItems = Array.from(items).map((item, index) => {
                // Clean up the content
                let content = item.innerHTML;
                
                // Remove Wingdings symbols and their containers
                content = content.replace(/<span style="font-family:Wingdings;">§<\/span>/g, '');
                
                // Clean up any remaining special characters
                content = content.replace(/&#160;/g, ' ');
                content = content.replace(/&amp;/g, '&');
                
                // Remove any empty spans
                content = content.replace(/<span[^>]*><\/span>/g, '');
                
                return {
                    id: index,
                    content: content
                };
            });
            
            return parsedItems;
        } catch (error) {
            // Error parsing updates, return fallback content
            return [{
                id: 0,
                content: htmlContent
            }];
        }
    };



    // 🌟 QUANTUM AUTO-SCROLL - Only scrolls when observed! 🌟
    useEffect(() => {
        // Only auto-scroll if: enabled + visible + tab active + not hovering
        const shouldScroll = isAutoScrolling && isVisible && isTabActive && !isHovering;
        
        if (!shouldScroll) return;
        
        const interval = setInterval(() => {
            if (roundupRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = roundupRef.current;
                if (scrollHeight - scrollTop <= clientHeight + 1) {
                    roundupRef.current.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                } else {
                    roundupRef.current.scrollBy({
                        top: 1,
                        behavior: 'auto'
                    });
                }
            }
        }, 80);

        return () => clearInterval(interval);
    }, [isHovering, isAutoScrolling, isVisible, isTabActive]);

    // 👁️ Intersection Observer - Detects when component is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            {
                threshold: 0.3, // Component must be 30% visible
                rootMargin: '0px 0px -50px 0px' // Bit of margin for better UX
            }
        );

        if (roundupRef.current) {
            observer.observe(roundupRef.current);
        }

        return () => {
            if (roundupRef.current) {
                observer.unobserve(roundupRef.current);
            }
        };
    }, []);

    // 👀 Page Visibility API - Detects when tab is active
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabActive(!document.hidden);
        };

        const handleFocus = () => setIsTabActive(true);
        const handleBlur = () => setIsTabActive(false);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    /**
     * Extracts bold topic headers from HTML content
     * 
     * @param {string} htmlString - HTML content to parse
     * @returns {Array} Array of extracted topic names
     */
    const extractBoldTopics = (htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const boldElements = doc.getElementsByTagName('b');
        return Array.from(boldElements).map(b => {
            let topic = b.textContent.trim()
                .replace(/:\s*$/, '')
                .replace(/\s+-\s+/, ': ')
                .replace(/\s+/g, ' ');
            
            if (topic.includes('Tokenisation')) return 'Tokenisation';
            if (topic.includes('ESG') || topic.includes('SFDR')) return 'ESG/SFDR';
            if (topic.includes('Innovation')) return 'Innovation';
            if (topic.includes('Corp')) return 'Corporate Sustainability';
            return topic;
        }).filter(topic => topic && topic.length > 1);
    };

    /**
     * Organizes data by topics extracted from content
     * 
     * @returns {Object} Object with topics as keys and arrays of related content as values
     */
    const getTopicBasedData = () => {
        const topicMap = {};
        
        sortedData.forEach(categoryData => {
            const updates = parseUpdates(categoryData.update);
            updates.forEach(update => {
                const topics = extractBoldTopics(update.content);
                topics.forEach(topic => {
                    if (!topicMap[topic]) {
                        topicMap[topic] = [];
                    }
                    topicMap[topic].push({
                        content: update.content,
                        category: categoryData.category,
                        colour: categoryData.colour
                    });
                });
            });
        });

        return topicMap;
    };

    /**
     * Removes header elements from HTML content for cleaner display
     * 
     * Removes bold headers ending with colons and department/organization headers.
     * Cleans up formatting while preserving content structure.
     * 
     * @param {string} htmlContent - HTML content to clean
     * @returns {string} Cleaned HTML content
     */
    const removeHeadersForDisplay = (htmlContent) => {
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = htmlContent;
        
        // Find and remove all bold elements that end with colons (headers)
        const boldElements = tempContainer.querySelectorAll('b, strong');
        boldElements.forEach(boldEl => {
            const text = boldEl.textContent.trim();
            if (text.endsWith(':')) {
                boldEl.remove();
            }
        });
        
        // Also look for text that starts with a department/organization name followed by colon
        const textNodes = tempContainer.querySelectorAll('*');
        textNodes.forEach(node => {
            if (node.textContent.includes(':')) {
                const text = node.textContent.trim();
                // Check if it starts with common department/organization patterns
                if (text.match(/^[A-Z][a-zA-Z\s&]+:/)) {
                    // If this node only contains the header text, remove it
                    if (node.textContent.trim() === text) {
                        node.remove();
                    }
                }
            }
        });
        
        // Remove formatting from headers that match topic names but keep the text
        const allElements = tempContainer.querySelectorAll('*');
        allElements.forEach(element => {
            const text = element.textContent.trim();
            
            // Check if this element contains header text that matches our topic structure
            const isHeaderText = text.match(/^[A-Z][a-zA-Z\s&()]+$/) && text.length > 5;
            
            if (isHeaderText) {
                // Remove all styling but keep the text
                element.removeAttribute('style');
                element.removeAttribute('class');
                
                // If it's a span or other formatting element, replace it with plain text
                if (element.tagName === 'SPAN' || element.tagName === 'STRONG' || element.tagName === 'B') {
                    // Check if there's content after this element that needs a space
                    const nextSibling = element.nextSibling;
                    const needsSpace = nextSibling && nextSibling.nodeType === Node.TEXT_NODE && 
                                      nextSibling.textContent.trim() && 
                                      !nextSibling.textContent.startsWith(' ');
                    
                    const plainText = document.createTextNode(text + (needsSpace ? ' ' : ''));
                    element.parentNode.replaceChild(plainText, element);
                }
            }
        });
        
        // Clean up any leading line breaks that might be left
        let content = tempContainer.innerHTML;
        content = content.replace(/^(\s*<br\s*\/?>)+/i, '');
        content = content.replace(/^(\s*<\/p>\s*<p[^>]*>)+/i, '');
        content = content.trim();
        
        return content;
    };


    return (
        <div className="monthly-roundup-container">
            <div className="monthly-roundup"
                ref={roundupRef}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <div className="monthly-updates">
                    {Object.entries(getTopicBasedData()).map(([topic, items]) => (
                        <div key={topic} className="category-section">
                            <div className="category-header">
                                <div className="header-content">
                                    <span className="category-title" style={{ color: 'white' }}>
                                        {topic}
                                    </span>
                                </div>
                            </div>
                            {items.map((item, index) => (
                                <div 
                                    key={index} 
                                    className="update-item"
                                    style={{borderLeft: `4px solid ${getColor(item.colour)}`}}
                                >
                                    {Parse(removeHeadersForDisplay(item.content))}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
};

export default MonthlyRoundup; 